// @ts-strict-ignore
import * as React from 'react';
import {
    OverviewDestinationStatus,
    DestinationStatusOrderIndices,
    WorkflowRunOverviewDisplayStatus,
} from '@/types/workflowOverview';
import { displayTextForOverviewDestinationStatus } from '@/helpers/displayHelpers';
import { sortBy } from 'lodash';
import { Destination } from '@/types/destinations';
import { DestinationPhasedRelease } from '@/types';
import { HotfixStatusTag, RunStatusTag } from './widgets/WorkflowRunOverviewStatus';
import {
    DestinationStatusDataSourceItem,
    DestinationStatusPopoverContent,
    DestinationStatusTagWidget,
} from './widgets/WorkflowRunDestinationStatus';
import Popover from 'antd/lib/popover';

interface WorkflowRunStatusWidgetsContainerProps {
    overviewDisplayStatus: WorkflowRunOverviewDisplayStatus;
    allDestinations: Destination[];
    selectedDestinationStepIds: string[];
    statusesByDestinationStepId: Record<string, OverviewDestinationStatus>;
    phasedInfoByDestinationStepId?: Record<string, DestinationPhasedRelease>;
    reviewStartedDateByDestinationStepId?: Record<string, string>;
    legacyReviewStartedDate?: string;
    isHotfix?: boolean;
}

export const WorkflowRunStatusWidgetsContainer: React.FC<WorkflowRunStatusWidgetsContainerProps> = ({
    overviewDisplayStatus,
    allDestinations,
    selectedDestinationStepIds,
    statusesByDestinationStepId,
    phasedInfoByDestinationStepId,
    reviewStartedDateByDestinationStepId,
    legacyReviewStartedDate,
    isHotfix,
}) => {
    const rawDestinationStatusDataSource: DestinationStatusDataSourceItem[] = React.useMemo(() => {
        const dataSource: DestinationStatusDataSourceItem[] = [];

        for (const destinationStepId of selectedDestinationStepIds) {
            const matchingDestination = allDestinations.find((dest) => dest.stepConfigId === destinationStepId);
            const matchingStatus = statusesByDestinationStepId[destinationStepId];

            const haveMatchingDestination = !!matchingDestination;
            const haveMatchingStatus = !!matchingStatus;

            const maybePhasedReleasePercent = phasedInfoByDestinationStepId?.[destinationStepId]?.phasedReleasePercent;

            const maybeReviewStartedAt =
                !!reviewStartedDateByDestinationStepId && !!reviewStartedDateByDestinationStepId[destinationStepId] ?
                    reviewStartedDateByDestinationStepId[destinationStepId]
                : legacyReviewStartedDate ? legacyReviewStartedDate
                : null;

            if (haveMatchingDestination && haveMatchingStatus) {
                dataSource.push({
                    destinationStepId: destinationStepId,
                    status: matchingStatus,
                    destination: matchingDestination,
                    displayText: displayTextForOverviewDestinationStatus(
                        matchingStatus,
                        matchingDestination.platform,
                        matchingDestination.appStoreType,
                        maybePhasedReleasePercent,
                    ),
                    maybeReviewStartedAt: maybeReviewStartedAt,
                });
            }
        }

        return dataSource;
    }, [selectedDestinationStepIds, allDestinations]);

    const sortedStatusDataSource = React.useMemo(() => {
        return sortBy(rawDestinationStatusDataSource, (rawDatum) => {
            return DestinationStatusOrderIndices[rawDatum.status];
        });
    }, [rawDestinationStatusDataSource]);

    const isSkippedStatus = overviewDisplayStatus === WorkflowRunOverviewDisplayStatus.Skipped;
    const shouldLightenTagContent = isSkippedStatus;

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                alignContent: 'center',
                width: '100%',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'stretch',
                    gap: 12,
                }}
            >
                <Popover
                    content={
                        <DestinationStatusPopoverContent
                            destinationStatusDataSource={sortedStatusDataSource}
                            shouldRenderStatusData
                        />
                    }
                >
                    <>
                        <RunStatusTag
                            overviewDisplayStatus={overviewDisplayStatus}
                            shouldRenderEllipsis={selectedDestinationStepIds.length > 1}
                        />
                    </>
                </Popover>
                {!!isHotfix && <HotfixStatusTag shouldLightenTagContent={shouldLightenTagContent} />}
            </div>
            <DestinationStatusTagWidget
                statusDataSource={rawDestinationStatusDataSource}
                allDestinations={allDestinations}
            />
        </div>
    );
};
